import { useEffect, useRef, type FC } from "react";

/**
 * Renders the "Ona Conversations 2.0" promotional canvas animation.
 * Spinning Ona lettermark with satellite panels that pop in progressively,
 * connection lines, traveling particles, and an animated dot-grid background.
 *
 * The canvas runs at the element's CSS pixel size × devicePixelRatio and
 * uses requestAnimationFrame with wall-clock timing for frame-rate-independent
 * playback. When `playing` is false the loop stops and the time counter
 * resets so the intro sequence replays on next start.
 */
export const SuperOnaAnimation: FC<{
    /** CSS class applied to the outer wrapper div. */
    className?: string;
    /** Whether the animation should be running. Resets to intro on restart. */
    playing?: boolean;
}> = ({ className, playing = true }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        if (!playing) {
            cancelAnimationFrame(rafRef.current);
            return;
        }

        // Size the canvas backing store to match the element's CSS pixel size
        // multiplied by devicePixelRatio for sharp rendering.
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        // Wall-clock timing so the animation speed is frame-rate independent.
        const startMs = performance.now();
        const loop = () => {
            const elapsed = (performance.now() - startMs) / 1000;
            // Scale to match the original animation speed (0.008 per frame at ~60fps ≈ 0.48/s)
            const time = elapsed * 0.48;
            draw(ctx, canvas.width, canvas.height, time);
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
        };
    }, [playing]);

    return (
        <div className={className}>
            <canvas ref={canvasRef} className="size-full rounded-[inherit]" />
        </div>
    );
};

// ─── Drawing functions (ported verbatim from super-ona-is-here.html) ────────

function drawBase(ctx: CanvasRenderingContext2D, cw: number, ch: number, time: number) {
    const g = ctx.createLinearGradient(0, 0, cw, ch);
    g.addColorStop(0, "#C5D4FE");
    g.addColorStop(1, "#6A93FF");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cw, ch);

    const gap = cw / 50;
    const cols = Math.ceil(cw / gap) + 1;
    const rows = Math.ceil(ch / gap) + 1;
    const db = cw / 1400;

    for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
            const x = i * gap,
                y = j * gap;
            const nx = x / cw,
                ny = y / ch;
            const c =
                (Math.sin(Math.sqrt((nx - 0.25) ** 2 + (ny - 0.45) ** 2) * 15 - time * 1.1) * 0.5 + 0.5) * 0.45 +
                (Math.sin(Math.sqrt((nx - 0.7) ** 2 + (ny - 0.25) ** 2) * 18 - time * 0.75 + 1.5) * 0.5 + 0.5) * 0.3 +
                (Math.sin(Math.sqrt((nx - 0.55) ** 2 + (ny - 0.8) ** 2) * 12 - time * 0.9 + 3) * 0.5 + 0.5) * 0.25;
            ctx.beginPath();
            ctx.arc(x, y, db * (1 + c * 2.5), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255,255,255,${0.075 + c * 0.3375})`;
            ctx.fill();
        }
    }
}

function drawOnaLogo(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, alpha: number) {
    const s = size / 30;

    ctx.save();
    ctx.translate(cx - 15 * s, cy - 15 * s);
    ctx.scale(s, s);

    // Outer path
    ctx.beginPath();
    ctx.moveTo(18.8838, 0);
    ctx.bezierCurveTo(18.8838 + 0.9013, 0, 21.2451 - 0.6008, 1.02832 - 0.6429, 21.2451, 1.02832);
    ctx.bezierCurveTo(21.2451 + 2.5892, 1.02832 + 2.5859, 29.0127 - 2.5892, 8.78613 - 2.5859, 29.0127, 8.78613);
    ctx.bezierCurveTo(29.0127 + 0.6007, 8.78613 + 0.5999, 30 - 0.0001, 11.1426 - 0.9427, 30, 11.1426);
    ctx.bezierCurveTo(30, 11.1426 + 2.5859, 30, 18.9004 - 2.5859, 30, 18.9004);
    ctx.bezierCurveTo(30 - 0.0001, 18.9004 + 0.9426, 29.0127 + 0.6007, 21.2568 - 0.5999, 29.0127, 21.2568);
    ctx.bezierCurveTo(29.0127 - 2.5892, 21.2568 + 2.5859, 21.2451 + 2.5892, 29.0146 - 2.5859, 21.2451, 29.0146);
    ctx.bezierCurveTo(21.2451 - 0.6008, 29.0146 + 0.6429, 18.8838 + 0.9013, 30, 18.8838, 30);
    ctx.bezierCurveTo(18.8838 - 2.5892, 30, 11.1162 + 2.5892, 30, 11.1162, 30);
    ctx.bezierCurveTo(11.1162 - 0.944, 30, 8.71289 + 0.6437, 29.0146 + 0.6426, 8.71289, 29.0146);
    ctx.bezierCurveTo(8.71289 - 2.5895, 29.0146 - 2.5859, 0.944336 + 2.5895, 21.2568 + 2.5859, 0.944336, 21.2568);
    ctx.bezierCurveTo(0.944336 - 0.6007, 21.2568 - 0.5999, 0 + 0.0001, 18.9004 + 0.9426, 0, 18.9004);
    ctx.bezierCurveTo(0, 18.9004 - 2.5859, 0, 11.1426 + 2.5859, 0, 11.1426);
    ctx.bezierCurveTo(0 + 0.0001, 11.1426 - 0.9427, 0.944336 - 0.6007, 8.74316 + 0.6428, 0.944336, 8.74316);
    ctx.bezierCurveTo(0.944336 + 2.5895, 8.74316 - 2.5859, 8.71289 - 2.5895, 0.985352 + 2.5859, 8.71289, 0.985352);
    ctx.bezierCurveTo(8.71289 + 0.6437, 0.985352 - 0.5998, 11.1162 - 0.944, 0, 11.1162, 0);
    ctx.bezierCurveTo(11.1162 + 2.5892, 0, 18.8838 - 2.5892, 0, 18.8838, 0);
    ctx.closePath();

    // Inner cutout
    ctx.moveTo(10.5996, 7);
    ctx.bezierCurveTo(10.5996 - 1.9879, 7 + 0.0002, 7 + 0.0002, 10.5996 - 1.9879, 7, 10.5996);
    ctx.bezierCurveTo(7, 10.5996 + 2.9336, 7, 19.4004 - 2.9336, 7, 19.4004);
    ctx.bezierCurveTo(7 + 0.0002, 19.4004 + 1.9879, 10.5996 - 1.9879, 23 - 0.0002, 10.5996, 23);
    ctx.bezierCurveTo(10.5996 + 2.9336, 23, 19.4004 - 2.9336, 23, 19.4004, 23);
    ctx.bezierCurveTo(19.4004 + 1.9879, 23 - 0.0002, 23 - 0.0002, 19.4004 + 1.9879, 23, 19.4004);
    ctx.bezierCurveTo(23, 19.4004 - 2.9336, 23, 10.5996 + 2.9336, 23, 10.5996);
    ctx.bezierCurveTo(23 - 0.0002, 10.5996 - 1.9879, 19.4004 + 1.9879, 7 + 0.0002, 19.4004, 7);
    ctx.bezierCurveTo(19.4004 - 2.9336, 7, 10.5996 + 2.9336, 7, 10.5996, 7);
    ctx.closePath();

    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.fill("evenodd");

    ctx.restore();
}

function drawOverlay(ctx: CanvasRenderingContext2D, cw: number, ch: number, time: number) {
    const u = ch / 960;
    const cx = cw / 2,
        cy = ch / 2;

    // Central glow
    const logoSize = 140 * u;
    const glowPulse = 1 + Math.sin(time * 1.2) * 0.08;
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 250 * u * glowPulse);
    glow.addColorStop(0, "rgba(255,255,255,0.25)");
    glow.addColorStop(0.4, "rgba(255,255,255,0.08)");
    glow.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 250 * u * glowPulse, 0, Math.PI * 2);
    ctx.fill();

    // Rotation logic
    const introSpinDuration = 1.5;
    const introEnd = 3 * introSpinDuration;
    const completedIntroSpins = Math.min(Math.floor(time / introSpinDuration), 3);
    const inIdlePhase = time >= introEnd;

    let rotation = 0;
    let liftY = 0;

    if (!inIdlePhase) {
        const spinTime = time % introSpinDuration;
        const spinPhase = spinTime / introSpinDuration;
        if (spinPhase < 0.6) {
            const t = spinPhase / 0.6;
            const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            rotation = Math.PI * e;
        } else {
            rotation = Math.PI;
        }
        rotation += completedIntroSpins * Math.PI;
    } else {
        rotation = 3 * Math.PI;
        liftY = Math.sin(time * 0.8) * 6 * u;
        const idleTime = time - introEnd;
        const idleCycleDuration = 5.0;
        const idlePhase = (idleTime % idleCycleDuration) / idleCycleDuration;
        if (idlePhase < 0.3) {
            const t = idlePhase / 0.3;
            const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            rotation += Math.PI * e;
        } else {
            rotation += Math.PI;
        }
        const idleFullCycles = Math.floor(idleTime / idleCycleDuration);
        rotation += idleFullCycles * Math.PI;
    }

    ctx.save();
    ctx.translate(cx, cy + liftY);
    ctx.rotate(rotation);
    ctx.translate(-cx, -(cy + liftY));
    drawOnaLogo(ctx, cx, cy + liftY, logoSize, 0.85);
    ctx.restore();

    // Ring
    ctx.beginPath();
    ctx.arc(cx, cy + liftY, logoSize * 0.85, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${0.15 + Math.sin(time * 0.8) * 0.05})`;
    ctx.lineWidth = 1.5 * u;
    ctx.stroke();

    // Panels
    const panels = [
        { angle: -0.5, dist: 300, w: 150, h: 95, speed: 0.12, lines: 3, appearAfterSpin: 1 },
        { angle: 2.0, dist: 330, w: 170, h: 90, speed: -0.1, lines: 3, appearAfterSpin: 2 },
        { angle: -2.2, dist: 290, w: 130, h: 105, speed: 0.15, lines: 4, appearAfterSpin: 2 },
        { angle: 0.7, dist: 350, w: 160, h: 80, speed: -0.08, lines: 2, appearAfterSpin: 3 },
        { angle: 3.5, dist: 340, w: 125, h: 92, speed: 0.11, lines: 3, appearAfterSpin: 3 },
        { angle: 4.8, dist: 310, w: 140, h: 100, speed: -0.13, lines: 3, appearAfterSpin: 3 },
    ];

    for (let p = 0; p < panels.length; p++) {
        const panel = panels[p];
        const appearTime = (panel.appearAfterSpin - 1) * introSpinDuration + introSpinDuration * 0.48;
        const timeSinceAppear = time - appearTime;
        if (timeSinceAppear < 0) continue;

        const fadeProgress = Math.min(timeSinceAppear / 0.2, 1.0);
        const c1 = 1.70158;
        const c3 = c1 + 1;
        const popEased = 1 + c3 * Math.pow(fadeProgress - 1, 3) + c1 * Math.pow(fadeProgress - 1, 2);
        const panelAlpha = Math.min(popEased, 1.0);
        const panelScale = 0.4 + 0.6 * popEased;

        const a = panel.angle + time * panel.speed;
        const wobble = Math.sin(time * 0.5 + p * 2.3) * 12 * u;
        const px = cx + Math.cos(a) * (panel.dist * u * panelScale + wobble);
        const py = cy + Math.sin(a) * (panel.dist * u * 0.65 * panelScale + wobble * 0.4);
        const spw = panel.w * u * panelScale;
        const sph = panel.h * u * panelScale;
        const spr = 8 * u;

        // Connection line
        const ringRadius = logoSize * 0.85;
        const angleToPanel = Math.atan2(py - (cy + liftY), px - cx);
        const ringX = cx + Math.cos(angleToPanel) * ringRadius;
        const ringY = cy + liftY + Math.sin(angleToPanel) * ringRadius;

        const angleToPanelFromRing = Math.atan2(ringY - py, ringX - px);
        const hw = spw / 2,
            hh = sph / 2;
        const cosA = Math.cos(angleToPanelFromRing),
            sinA = Math.sin(angleToPanelFromRing);
        const sx = cosA !== 0 ? Math.abs(hw / cosA) : Infinity;
        const sy = sinA !== 0 ? Math.abs(hh / sinA) : Infinity;
        const edgeDist = Math.min(sx, sy);
        const edgeX = px + cosA * edgeDist;
        const edgeY = py + sinA * edgeDist;

        const lineAlpha = (0.15 + Math.sin(time * 1.2 + p * 1.1) * 0.05) * panelAlpha;
        ctx.beginPath();
        ctx.moveTo(ringX, ringY);
        ctx.lineTo(edgeX, edgeY);
        ctx.strokeStyle = `rgba(255,255,255,${lineAlpha})`;
        ctx.lineWidth = 1 * u;
        ctx.stroke();

        // Anchor dots
        ctx.beginPath();
        ctx.arc(ringX, ringY, 3 * u, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.6 * panelAlpha})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(edgeX, edgeY, 2.5 * u, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.5 * panelAlpha})`;
        ctx.fill();

        // Traveling particles
        for (let pi = 0; pi < 2; pi++) {
            const t = (time * 0.35 + p * 0.7 + pi * 0.5) % 1;
            const partX = ringX + (edgeX - ringX) * t;
            const partY = ringY + (edgeY - ringY) * t;
            const partAlpha = Math.sin(t * Math.PI) * 0.7 * panelAlpha;
            ctx.beginPath();
            ctx.arc(partX, partY, 2 * u, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255,255,255,${partAlpha})`;
            ctx.fill();
        }

        // Panel glow
        const sg = ctx.createRadialGradient(px, py, 0, px, py, spw * 0.8);
        sg.addColorStop(0, `rgba(255,255,255,${0.06 * panelAlpha})`);
        sg.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = sg;
        ctx.beginPath();
        ctx.arc(px, py, spw * 0.8, 0, Math.PI * 2);
        ctx.fill();

        // Panel body
        ctx.beginPath();
        ctx.roundRect(px - spw / 2, py - sph / 2, spw, sph, spr);
        ctx.fillStyle = `rgba(255,255,255,${(0.28 + Math.sin(time * 0.6 + p * 1.5) * 0.04) * panelAlpha})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(255,255,255,${(0.45 + Math.sin(time * 0.9 + p) * 0.1) * panelAlpha})`;
        ctx.lineWidth = 1.2 * u;
        ctx.stroke();

        // Content lines
        for (let l = 0; l < panel.lines; l++) {
            const ly = py - sph / 2 + 14 * u * panelScale + l * 13 * u * panelScale;
            const lw = spw * (0.4 + Math.sin(l * 2.3 + time * 0.5 + p) * 0.2);
            ctx.beginPath();
            ctx.roundRect(px - spw / 2 + 10 * u * panelScale, ly, lw, 4 * u * panelScale, 2 * u);
            ctx.fillStyle = `rgba(255,255,255,${(0.35 + Math.sin(time * 0.7 + l + p) * 0.08) * panelAlpha})`;
            ctx.fill();
        }
    }

    // Ambient particles
    for (let i = 0; i < 25; i++) {
        const seed1 = Math.sin(i * 127.1 + 42) * 0.5 + 0.5;
        const seed2 = Math.sin(i * 311.7 + 42) * 0.5 + 0.5;
        const particleFade = Math.min(time / 8, 1);
        const bx = (seed1 - 0.5) * cw * 0.8 + cx;
        const by = (seed2 - 0.5) * ch * 0.8 + cy;
        const ox = Math.sin(time * 0.25 + i * 2.1) * 18 * u;
        const oy = Math.cos(time * 0.2 + i * 1.7) * 14 * u;
        const fpx = bx + ox;
        const fpy = by + oy;
        const r = (1.5 + seed1 * 2) * u;
        const fa = (0.12 + Math.sin(time * 0.8 + i * 0.9) * 0.08) * particleFade;

        ctx.beginPath();
        ctx.arc(fpx, fpy, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${fa})`;
        ctx.fill();
    }
}

function draw(ctx: CanvasRenderingContext2D, cw: number, ch: number, time: number) {
    ctx.clearRect(0, 0, cw, ch);
    drawBase(ctx, cw, ch, time);
    drawOverlay(ctx, cw, ch, time);
}
